import AppLink from "@/integrations/next/components/AppLink"
import AnimateOnEnter from "@/ui/animations/AnimateOnEnter"
import Typography from "@/ui/atoms/Typography"
import Carousel from "@/ui/molecules/Carousel"
import Square from "@/ui/shapes/Square"
import Container from "@/ui/structure/Container"
import { CtaData } from "@/ui/types/cta"
import { classNames, setCssVar } from "@punks/ui-core"
import { ReactNode } from "react"

export interface MultiCarouselSlide {
  content: ReactNode
  link?: string
  linkTarget?: string
}

export type CarouselColor = "paper" | "grey" | "negative" | "primary"

export interface MultiCarousel {
  slides: MultiCarouselSlide[]
  title?: ReactNode
  cta?: CtaData
  maxSlidesXs: number
  maxSlidesMd: number
  color?: CarouselColor
  width?: string
}

export interface MultiCarouselProps {
  carousels: MultiCarousel[]
}

const MultiCarousel = ({ carousels }: MultiCarouselProps) => {
  // usePageMenuPainter(
  //   ref,
  //   carousels[carousels.length - 1].color === "negative"
  //     ? "negative"
  //     : "default"
  // )
  return (
    <div className="relative">
      <div
        className={classNames(
          "hidden xl:block absolute left-0 top-0 bottom-0 w-[50%] z-[-1]",
          {
            "bg-paper": carousels[0].color === "paper",
            "bg-background": carousels[0].color === "grey",
            "bg-primary": carousels[0].color === "primary",
            "bg-negative-contrast": carousels[0].color === "negative",
          }
        )}
      ></div>
      <div
        className={classNames(
          "hidden xl:block absolute right-0 top-0 bottom-0 w-[50%] z-[-1]",
          {
            "bg-paper": carousels[carousels.length - 1].color === "paper",
            "bg-background": carousels[carousels.length - 1].color === "grey",
            "bg-primary": carousels[carousels.length - 1].color === "primary",
            "bg-negative-contrast":
              carousels[carousels.length - 1].color === "negative",
          }
        )}
      ></div>
      <Container className="flex flex-col xl:flex-row" noGuttersLg>
        {carousels.map((carousel, i) => (
          <div
            key={i}
            style={{
              ...setCssVar("--w", carousel.width),
            }}
            className={classNames(
              "w-full xl:w-[var(--w)] pt-16 pb-8 xl:pt-32 xl:pb-24",
              {
                "bg-paper": carousel.color === "paper",
                "bg-background": carousel.color === "grey",
                "bg-primary text-primary-contrast":
                  carousel.color === "primary",
                "bg-negative-contrast text-negative":
                  carousel.color === "negative",
                "xl:pr-16": i === 0,
                "xl:pl-16": i > 0,
              }
            )}
          >
            <div className="px-container xl:px-0">
              <Typography
                variant={"subtitle2"}
                textTransform={"uppercase"}
                weight={"bold"}
                inheritColor
                textAlign={"center"}
              >
                {carousel.title}
              </Typography>
            </div>
            <div className={"mt-8 mb-16"}>
              <AnimateOnEnter>
                <Carousel
                  slides={carousel.slides.map((x) => ({
                    content: (
                      <AppLink href={x.link} target={x.linkTarget}>
                        <Square contentClassName="flex items-center justify-center p-2">
                          {x.content}
                        </Square>
                      </AppLink>
                    ),
                  }))}
                  defaultSettings={{
                    slidesPerView: carousel.maxSlidesXs ?? 1,
                    slidesOffsetBefore: 0,
                    slidesOffsetAfter: 24,
                  }}
                  responsiveSettings={{
                    sm: {
                      slidesPerView: carousel.maxSlidesMd,
                      slidesOffsetBefore: 0,
                      slidesOffsetAfter: 0,
                    },
                  }}
                  bars="bars"
                  barsColor={
                    carousel.color === "negative" ||
                    carousel.color === "primary"
                      ? "paper"
                      : "primary"
                  }
                />
              </AnimateOnEnter>
            </div>
          </div>
        ))}
      </Container>
    </div>
  )
}

export default MultiCarousel
